import React from 'react';
import { Link } from 'react-router-dom';
import styles from '../App.module.css';
import FooterLinks from '../components/layout/FooterLinks';
import BurgerHint from '../components/layout/BurgerHint';
import ThreeBenefits from '../components/layout/ThreeBenefits';

const SplashPage = ({ theme }) => {
  const mutedText = theme === 'dark' ? '#d1d5db' : '#4b5563';

  return (
    <div className={`${styles.container} ${theme === 'dark' ? styles.darkBackground : styles.lightBackground}`}>
      <div className={styles.staticContentBox}>
        <h1 className={styles.pageHeading}>
          🌼 Welcome to{' '}
          <span style={{ fontFamily: 'Arial, sans-serif', fontSize: '2rem' }}>
            <span style={{ color: '#6b7280' }}>d</span>
            <span style={{ color: '#1d4ed8', fontWeight: 'bold' }}>AI</span>
            <span style={{ color: '#6b7280' }}>sy</span>
          </span>
        </h1>

        {/* Burger Tip */}
        <BurgerHint />

        {/* Primary Actions + Collapsed Quick Links */}
        <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '0.75rem',
              flexWrap: 'wrap',
              marginBottom: '1rem',
            }}
          >
            <Link
              to="/chat"
              style={{
                display: 'inline-block',
                padding: '0.65rem 1rem',
                borderRadius: '999px',
                background: '#1d4ed8',
                color: '#ffffff',
                textDecoration: 'none',
                fontWeight: 'bold',
                fontSize: '0.95rem',
              }}
            >
              💬 Chat with dAIsy
            </Link>

            <Link
              to="/register"
              style={{
                display: 'inline-block',
                padding: '0.65rem 1rem',
                borderRadius: '999px',
                background: '#f3f4f6',
                color: '#111827',
                textDecoration: 'none',
                fontWeight: 'bold',
                fontSize: '0.95rem',
                border: '1px solid #d1d5db',
              }}
            >
              🧠 Register
            </Link>
          </div>

          <details
            style={{
              maxWidth: '520px',
              margin: '0 auto',
              textAlign: 'left',
              border: '1px solid #d1d5db',
              borderRadius: '12px',
              padding: '0.75rem 1rem',
              background: theme === 'dark' ? 'rgba(31, 41, 55, 0.65)' : '#ffffff',
            }}
          >
            <summary
              style={{
                cursor: 'pointer',
                fontWeight: 'bold',
                color: theme === 'dark' ? '#f9fafb' : '#111827',
              }}
            >
              Quick links
            </summary>

            <ul
              style={{
                listStyle: 'none',
                padding: 0,
                margin: '1rem 0 0 0',
              }}
            >
              <li style={{ marginBottom: '0.75rem' }}>
                🌱 <Link to="/about">About</Link> - What makes <strong>dAIsy</strong> so unique?
              </li>
              <li style={{ marginBottom: '0.75rem' }}>
                🔒 <Link to="/privacy">Privacy</Link> - Your data stays private.
              </li>
              <li style={{ marginBottom: '0.75rem' }}>
                ❓ <Link to="/faq">FAQ</Link> - Find quick answers.
              </li>
              <li style={{ marginBottom: '0.75rem' }}>
                🔐 <Link to="/login">Log In</Link> - Already a member? Welcome back.
              </li>
              <li style={{ marginBottom: '0.75rem' }}>
                💬 <Link to="/help">Help</Link> - Get personal support.
              </li>
              <li style={{ marginBottom: '0.75rem' }}>
                📞 <Link to="/contact">Contact</Link> - Reach out if you need us.
              </li>
              <li style={{ marginBottom: '0.75rem' }}>
                📜 <Link to="/terms">Terms</Link> - The simple legal stuff.
              </li>
              <li style={{ marginBottom: 0 }}>
                👤 <Link to="/founder">Founder</Link> - The story behind <strong>dAIsy</strong>.
              </li>
            </ul>
          </details>
        </div>

        {/* dAIsy Core Positioning Section */}
        <section
          style={{
            marginTop: '2rem',
            paddingTop: '1.5rem',
            borderTop: '1px solid #d1d5db',
            textAlign: 'left',
          }}
        >
          <h2 style={{ textAlign: 'center', marginBottom: '1rem' }}>
            🌼 dAIsy Core
          </h2>

          {/* Drift Stack Verified Badge */}
          <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
            <img
              src="/images/drift-stack-verified-certification-badge.png"
              alt="Drift Stack Verified badge for safe AI use"
              style={{
                width: '100%',
                maxWidth: '240px',
                borderRadius: '16px',
                boxShadow: '0 12px 32px rgba(0, 0, 0, 0.22)',
              }}
            />

            <p
              style={{
                fontSize: '0.9rem',
                color: mutedText,
                marginTop: '0.75rem',
                maxWidth: '520px',
                marginLeft: 'auto',
                marginRight: 'auto',
                lineHeight: 1.45,
                textAlign: 'center',
              }}
            >
              <strong>Drift Stack™ Verified</strong> means dAIsy is designed
              around controlled AI use: state awareness, relationship continuity,
              and execution discipline.
            </p>
          </div>

          <p>
            <strong>dAIsy is not just a chatbot.</strong>
          </p>

          <p>
            And she is not limited to being a personal companion.
          </p>

          <p>
            At her core, <strong>dAIsy</strong> is a state-aware, relationship-aware,
            execution-aware agent engine designed to maintain continuity across
            people, conversations, decisions, relationships, tasks, and next actions.
          </p>

          <p>
            That means <strong>dAIsy</strong> can support multiple vertical agents:
          </p>

          <ul style={{ paddingLeft: '1.25rem', marginTop: '1rem' }}>
            <li style={{ marginBottom: '0.75rem' }}>
              <strong>Scout</strong> — business intake, routing, scheduling,
              qualification, and follow-up.
            </li>
            <li style={{ marginBottom: '0.75rem' }}>
              <strong>dAIsy Care</strong> — home care, elder care, family
              coordination, reminders, and check-ins.
            </li>
            <li style={{ marginBottom: '0.75rem' }}>
              <strong>dAIsy Workforce</strong> — recruiting, onboarding, scheduling,
              employee support, and compliance workflows.
            </li>
            <li style={{ marginBottom: '0.75rem' }}>
              <strong>dAIsy Concierge</strong> — real estate, wineries, hospitality,
              tours, bookings, and client follow-up.
            </li>
            <li style={{ marginBottom: '0.75rem' }}>
              <strong>dAIsy Ops</strong> — internal workflow assistance for small
              companies.
            </li>
          </ul>

          <p>
            These are not separate products built from scratch.
          </p>

          <p>
            They are vertical wrappers around the same core architecture.
          </p>

          <p style={{ fontWeight: 'bold', textAlign: 'center', marginTop: '1.5rem' }}>
            dAIsy is a continuity engine for human workflows.
          </p>
        </section>

        {/* ✨ Three Benefits Section */}
        <ThreeBenefits />

        {/* Footer */}
        <FooterLinks />
      </div>
    </div>
  );
};

export default SplashPage;